import React, { useMemo } from 'react';
import { Download } from 'lucide-react';
import ColumnsPicker, { useVisibleColumns, type ColumnDef } from './ColumnsPicker';
import { formatNumber, formatPercent } from './chartUtils';
import type { AiDashboardFacilityRow } from '../../services/aiDashboardAnalyticsService';

function formatSeconds(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  if (value < 60) {
    return `${Math.round(value)}s`;
  }
  const mins = value / 60;
  return `${Math.round(mins * 10) / 10} min`;
}

function formatRate(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return formatPercent(value);
}

const AI_FACILITY_COLUMNS: ColumnDef<AiDashboardFacilityRow>[] = [
  { key: 'facility', label: 'Facility' },
  { key: 'processed', label: 'AI Processed Studies' },
  { key: 'normal', label: 'Normal Cases' },
  { key: 'abnormal', label: 'Abnormal Cases' },
  { key: 'success_rate', label: 'Success Rate' },
  { key: 'avg_time', label: 'Avg Processing Time' },
  { key: 'override_rate', label: 'Override Rate' },
];

interface AiFacilityResultsTableProps {
  rows: AiDashboardFacilityRow[];
  showFacilityColumn?: boolean;
  exporting?: boolean;
  onExport?: () => void;
}

export default function AiFacilityResultsTable({
  rows,
  showFacilityColumn = false,
  exporting = false,
  onExport,
}: Readonly<AiFacilityResultsTableProps>) {
  const columns = useMemo(
    () =>
      showFacilityColumn
        ? AI_FACILITY_COLUMNS
        : AI_FACILITY_COLUMNS.filter(col => col.key !== 'facility'),
    [showFacilityColumn]
  );
  const storageKey = showFacilityColumn
    ? 'analytics-ai-facility-columns-v1'
    : 'analytics-ai-facility-columns-v1-no-facility';
  const { visibleColumns, visibleKeys, toggleColumn } = useVisibleColumns(columns, storageKey);

  const renderCell = (key: string, row: AiDashboardFacilityRow) => {
    switch (key) {
      case 'facility':
        return <span className="analytics-facility-name">{row.facility_name}</span>;
      case 'processed':
        return formatNumber(row.total_processed_studies ?? 0);
      case 'normal':
        return formatNumber(row.normal_count ?? 0);
      case 'abnormal':
        return formatNumber(row.abnormal_count ?? 0);
      case 'success_rate':
        return formatRate(row.success_rate);
      case 'avg_time':
        return formatSeconds(row.avg_processing_seconds);
      case 'override_rate':
        return formatRate(row.disagreement_rate);
      default:
        return '—';
    }
  };

  return (
    <section className="analytics-section-card ai-facility-results-section">
      <div className="analytics-section-header">
        <h3 className="analytics-section-title">AI results by facility</h3>
        <div className="analytics-section-actions">
          <ColumnsPicker
            columns={columns}
            storageKey={storageKey}
            visibleKeys={visibleKeys}
            toggleColumn={toggleColumn}
          />
          {onExport ? (
            <button
              type="button"
              className="analytics-secondary-btn"
              onClick={onExport}
              disabled={exporting}
            >
              <Download size={14} />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          ) : null}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="analytics-empty">No facility results for the selected filters.</p>
      ) : (
        <div className="analytics-table-wrap ai-facility-results-table-wrap">
          <table className="analytics-table ai-facility-results-table">
            <thead>
              <tr>
                {visibleColumns.map(col => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.facility_id}>
                  {visibleColumns.map(col => (
                    <td key={col.key}>{renderCell(col.key, row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
